import { inject, Injectable } from '@angular/core';
import { environment } from '../../environments/environment';
import {
  BehaviorSubject,
  catchError,
  map,
  Observable,
  of,
  switchMap,
  tap,
} from 'rxjs';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Router } from '@angular/router';
import { ChatService } from '../chat/chat.service';
import { EmergencyAlertsService } from '../emergency-alerts.service';
import { LocalStorageItem } from '../../interfaces/local-storage-item';

@Injectable({
  providedIn: 'root',
})
export class AuthService {
  private apiUrl: string = `${environment.api}/BaseAuthentication/Login`;
  private api: string = `${environment.api}`;

  private currentUserSubject = new BehaviorSubject<any>(null);
  currentUser$: Observable<any> = this.currentUserSubject.asObservable();
  isTokenRefreshing: boolean = false;

  get currentUserValue() {
    return this.currentUserSubject.value;
  }

  private http = inject(HttpClient);
  private router = inject(Router);
  private chatService = inject(ChatService);
  private emergencyService = inject(EmergencyAlertsService);

  /*
   * Returns the value of the currentUser Behavior Subject
   */
  get currentUserDetails() {
    return this.currentUserSubject.getValue();
  }

  constructor() {}

  login(username: string, password: string): Observable<any> {
    return this.http
      .post(this.apiUrl, { username: username, password: password })
      .pipe(
        map((auth: any) => {
          const result = this.setAuthFromLocalStorage(auth, username);
          this.setCurrentUserSubject(auth);
          return result;
        }),
        switchMap(() => this.getUserByUsername(username)),
        catchError((err) => {
          console.error('err', err);
          throw err;
        })
      );
  }

  getUserByUsername(email: string): Observable<any> {
    const auth = this.getAuthFromLocalStorage();
    if (!auth || !auth.authToken) {
      return of(undefined);
    }

    return this.fetchUserByUsername(email, auth.authToken).pipe(
      map((user: any) => {
        if (user) {
          user.authToken = auth.authToken;
          if (email === 'cCenter') this.chatService.setCallSenderId(user.id);
          this.chatService.startConnection(auth.authToken);
        } else {
          this.logout();
        }
        return user;
      }),
      switchMap(() =>
        this.emergencyService.getAllEmergencyAlerts().pipe(
          map((alerts) => {
            this.emergencyService.setAlerts(alerts);
          })
        )
      ),
      switchMap(() =>
        this.emergencyService.getFiles().pipe(
          map((files) => {
            this.emergencyService.setFiles(files);
          })
        )
      )
    );
  }
  private setAuthFromLocalStorage(auth: any, username: string): boolean {
    auth.username = username;
    const localStorageItem: LocalStorageItem = {
      ...auth,
      username: username,
    };
    if (auth && auth.authToken) {
      localStorage.setItem('auth', JSON.stringify(localStorageItem));
      return true;
    }
    return false;
  }
  private setCurrentUserSubject(user: any) {
    this.currentUserSubject.next(user);
  }

  private getAuthFromLocalStorage(): LocalStorageItem | undefined {
    try {
      const lsValue = localStorage.getItem('auth');
      if (!lsValue) {
        return undefined;
      }

      const authData = JSON.parse(lsValue);
      return authData;
    } catch (error) {
      console.error(error);
      return undefined;
    }
  }

  private fetchUserByUsername(
    username: string,
    token: string
  ): Observable<any | undefined> {
    const httpHeaders = new HttpHeaders({
      Authorization: `Bearer ${token}`,
    });
    return this.http
      .get<any>(`${this.api}/User/username/${username}`, {
        headers: httpHeaders,
      })
      .pipe(catchError(() => of(undefined)));
  }

  isLoggedIn(): boolean {
    const localStorageItem = this.getAuthFromLocalStorage();
    return localStorageItem !== undefined;
  }

  getRefreshToken(): Observable<
    { authToken: string; refreshToken: string } | undefined
  > {
    const authToken = this.getAuthFromLocalStorage();
    if (authToken && authToken.refreshToken) {
      const headers = new HttpHeaders({
        'Content-Type': 'application/json',
        Accept: '*/*',
      });
      return this.http
        .post<{ authToken: string; refreshToken: string }>(
          `${this.api}/BaseAuthentication/refresh-token`,
          JSON.stringify(authToken.refreshToken),
          { headers }
        )
        .pipe(
          tap((token) =>
            this.saveRefreshToken(token.authToken, token.refreshToken)
          )
        );
    }
    return of(undefined);
  }
  saveRefreshToken(authToken: string, refreshToken: string) {
    const authData = this.getAuthFromLocalStorage();
    if (authData) {
      authData.authToken = authToken;
      authData.refreshToken = refreshToken;
      localStorage.setItem('auth', JSON.stringify(authData));
      this.setCurrentUserSubject(authData);
      //reconnect to chat
      this.chatService.startConnection(authData.authToken);
    }
  }

  logout() {
    localStorage.removeItem('auth');
    this.currentUserSubject.next(null);
    this.chatService.destroyConnection();
    this.router.navigate(['/login'], {
      queryParams: {},
    });
  }
}
